import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Award, SkipForward } from "lucide-react";
import { usePublicSettings } from "@/hooks/useSettings";

const BG_SRC = "/learnme-brand-bg.mp4";
const VO = { intro: "/brand-intro-vo.mp3", outro: "/brand-outro-vo.mp3" };
const CAPTION = {
  intro: "Prepare to master your next skill.",
  outro: "Knowledge owned. Welcome to the Annals.",
};
const INTRO_DURATION = 5200; // intro clip length; ends right after "Knowledge for Everyone"
const FADE = 1200;
const OUTRO_CLIP = 5100;   // let the clip's closing narration fully land
const OUTRO_HOLD = 2000;   // hold on the cinematic library, music at full volume
const OUTRO_FADE = 900;    // graceful fade to black
const OUTRO_TOTAL = OUTRO_CLIP + OUTRO_HOLD + OUTRO_FADE;

// Branded intro/outro sequence. Plays the brand video (its own audio as the
// music bed, faded) with a layered voiceover, then calls onDone after ~8s.
export function CourseBrand({ mode = "intro", onDone }) {
  const videoRef = useRef(null);
  const voRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const { data: settings } = usePublicSettings();
  const brandLogo = settings?.branding?.logo_url || "/learnme-logo.png";
  const brandWordmark = settings?.branding?.wordmark || "Learn Me";

  // Keep onDone in a ref so the timeline effect runs once per `mode` and its
  // finish timer isn't reset by parent re-renders (onDone is an inline arrow).
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  useEffect(() => {
    const v = videoRef.current;
    const vo = voRef.current;
    let raf, fadeTimer, done = false;
    const total = mode === "outro" ? OUTRO_TOTAL : INTRO_DURATION;
    const finish = () => { if (done) return; done = true; onDoneRef.current && onDoneRef.current(); };

    const start = async () => {
      // Always show the visual (muted autoplay is always allowed).
      try { await v.play(); } catch {}
      // Outro loops so the music bed + cinematic library carry through the 2s hold.
      v.loop = mode === "outro";
      // Best-effort: enable the video's own audio as the music bed + voiceover.
      try {
        v.muted = false;
        v.volume = mode === "outro" ? 0 : 0.7;
        await v.play();
        setMuted(false);
      } catch { v.muted = true; setMuted(true); }
      if (vo) { vo.volume = 1; try { await vo.play(); } catch {} }

      const t0 = performance.now();
      const tick = () => {
        const el = performance.now() - t0;
        if (!v.muted) {
          if (mode === "intro") {
            // intro: fade the bed out at the very end
            const p = Math.min(1, Math.max(0, (el - (total - FADE)) / FADE));
            v.volume = 0.7 * (1 - p);
          } else {
            // outro: fade in, hold at FULL volume through the mastery beat, then fade to black
            if (el < FADE) v.volume = el / FADE;
            else if (el > total - OUTRO_FADE)
              v.volume = Math.max(0, 1 - (el - (total - OUTRO_FADE)) / OUTRO_FADE);
            else v.volume = 1;
          }
        }
        if (el < total) raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    };
    start();

    // Intro ends when its clip finishes. The outro loops and ends on a fixed timer
    // (clip narration + 2s hold + fade) so the closing line is never clipped.
    if (mode === "intro") v.addEventListener("ended", finish);
    fadeTimer = setTimeout(finish, total);
    return () => { cancelAnimationFrame(raf); clearTimeout(fadeTimer); v.removeEventListener("ended", finish); };
  }, [mode]);

  return (
    <motion.div
      className="fixed inset-0 z-[100] bg-black flex items-center justify-center overflow-hidden"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      data-testid={`course-brand-${mode}`}
    >
      <video ref={videoRef} src={BG_SRC} className="absolute inset-0 w-full h-full object-cover"
        autoPlay muted={muted} playsInline preload="auto" />
      <audio ref={voRef} src={VO[mode]} preload="auto" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/30 to-black/75" />

      <div className="relative z-10 text-center px-6">
        <motion.div
          initial={{ opacity: 0, y: 24, scale: 0.94 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-col items-center justify-center gap-3 mb-6"
        >
          <img src={brandLogo} alt={brandWordmark} className="h-20 md:h-24 w-auto drop-shadow-[0_4px_24px_rgba(42,92,255,0.5)]" />
          <span className="font-display text-3xl md:text-4xl font-extrabold tracking-tighter text-white">{brandWordmark}</span>
        </motion.div>

        {mode === "outro" && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="inline-flex items-center gap-2 border border-primary/60 bg-primary/15 px-5 py-2 mb-5"
            data-testid="achievement-unlocked"
          >
            <Award className="h-5 w-5 text-primary" />
            <span className="uppercase tracking-[0.25em] text-sm font-bold text-white">Achievement Unlocked</span>
          </motion.div>
        )}

        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          transition={{ delay: mode === "outro" ? 1 : 0.6, duration: 0.8 }}
          className="text-white/85 text-lg md:text-2xl font-display font-semibold max-w-xl mx-auto"
        >
          {CAPTION[mode]}
        </motion.p>
      </div>

      <button
        onClick={() => onDone && onDone()}
        className="absolute top-6 right-6 z-20 inline-flex items-center gap-2 text-white/70 hover:text-white border border-white/25 hover:border-white/60 px-4 h-10 text-sm font-semibold transition-colors backdrop-blur-sm"
        data-testid={`skip-${mode}-button`}
      >
        Skip <SkipForward className="h-4 w-4" />
      </button>

      <motion.div
        className="absolute bottom-0 left-0 h-1 bg-primary z-20"
        initial={{ width: "0%" }} animate={{ width: "100%" }}
        transition={{ duration: (mode === "outro" ? OUTRO_TOTAL : INTRO_DURATION) / 1000, ease: "linear" }}
      />
    </motion.div>
  );
}
